import type {
  AudioChunk,
  CoreAvSpec,
  CoreLoadOptions,
  FrameCore,
  InputSnapshot,
  SystemId,
  VideoFrame,
} from '../types';
import { PAD } from '../types';
import { SYSTEMS } from '../systems';

const SAMPLE_RATE = 44100;

/**
 * 内蔵の検証用コア。ROM も WASM も要らずに
 * 「映像 → 音声 → 入力 → セーブステート」の全経路を実行できる。
 *
 * 実機コアを差し込む前でも UI/入力/PWA/ストレージの動作確認が完結するので、
 * CI とモバイル実機テストの土台として常に同梱しておく。
 *
 * 動作: 十字キーで自機を動かし、A/B で色と音程が変わる。
 */
export class TestPatternCore implements FrameCore {
  readonly id = 'test-pattern';
  readonly systems: readonly SystemId[] = ['gb', 'gba', 'nes', 'snes', 'psx', 'ws', 'psp'];

  private width = 256;
  private height = 240;
  private fps = 60;
  private framebuffer = new Uint16Array(0);
  private audioBuf = new Int16Array(0);

  private x = 0;
  private y = 0;
  private frameCount = 0;
  private phase = 0;
  private hue = 0;
  private romChecksum = 0;

  async init(): Promise<void> {
    /* 何も読み込む必要がない */
  }

  async load(opts: CoreLoadOptions): Promise<CoreAvSpec> {
    const sys = SYSTEMS[opts.system];
    this.width = sys.nativeWidth;
    this.height = sys.nativeHeight;
    this.fps = sys.fps;

    this.framebuffer = new Uint16Array(this.width * this.height);
    // 1 フレーム分の音声（ステレオ）
    this.audioBuf = new Int16Array(Math.ceil(SAMPLE_RATE / this.fps) * 2);

    // ROM 内容で初期色が変わるようにして「本当に読めているか」を目視確認できる
    const romBytes = opts.rom instanceof Uint8Array ? opts.rom : new Uint8Array(await opts.rom.arrayBuffer());
    this.romChecksum = 0;
    for (let i = 0; i < romBytes.length; i += 997) {
      this.romChecksum = (this.romChecksum + (romBytes[i] ?? 0)) & 0xffff;
    }
    this.hue = this.romChecksum & 0x1f;

    if (opts.sram && opts.sram.length >= 4) {
      const view = new DataView(opts.sram.buffer, opts.sram.byteOffset, opts.sram.byteLength);
      this.x = view.getInt16(0, true);
      this.y = view.getInt16(2, true);
    } else {
      this.x = Math.floor(this.width / 2);
      this.y = Math.floor(this.height / 2);
    }

    return {
      width: this.width,
      height: this.height,
      fps: this.fps,
      sampleRate: SAMPLE_RATE,
      pixelFormat: 'RGB565',
    };
  }

  runFrame(input: InputSnapshot): VideoFrame {
    const btn = input.buttons[0] ?? 0;
    const held = (b: keyof typeof PAD) => (btn & (1 << PAD[b])) !== 0;

    const speed = held('R') ? 4 : 2;
    if (held('LEFT')) this.x -= speed;
    if (held('RIGHT')) this.x += speed;
    if (held('UP')) this.y -= speed;
    if (held('DOWN')) this.y += speed;

    // アナログスティックも反映（PS1 想定の経路確認）
    const dead = 0.15;
    if (Math.abs(input.analog.leftX) > dead) this.x += input.analog.leftX * speed;
    if (Math.abs(input.analog.leftY) > dead) this.y += input.analog.leftY * speed;

    this.x = clamp(this.x, 4, this.width - 5);
    this.y = clamp(this.y, 4, this.height - 5);

    if (held('START')) this.hue = (this.hue + 1) & 0x1f;
    if (held('SELECT')) this.hue = (this.hue + 31) & 0x1f;

    this.renderFrame(held('A'), held('B'), held('X'), held('Y'));
    this.renderAudio(held('A'), held('B'));

    this.frameCount++;

    return {
      data: this.framebuffer,
      width: this.width,
      height: this.height,
      pitch: this.width * 2,
      format: 'RGB565',
    };
  }

  private renderFrame(a: boolean, b: boolean, x: boolean, y: boolean): void {
    const fb = this.framebuffer;
    const w = this.width;
    const h = this.height;
    const t = this.frameCount;

    // 背景: ゆっくり流れるグラデーション + チェッカー
    for (let py = 0; py < h; py++) {
      const row = py * w;
      for (let px = 0; px < w; px++) {
        const checker = ((px >> 4) + (py >> 4) + (t >> 5)) & 1;
        const r = ((px + t) >> 3) & 0x1f;
        const g = ((py - t) >> 2) & 0x3f;
        const bl = (this.hue + checker * 8) & 0x1f;
        fb[row + px] = ((r << 11) | (g << 5) | bl) >>> 0;
      }
    }

    // 自機（ボタンで色が変わる 9x9 のスプライト）
    const color =
      a ? rgb565(31, 0, 0) : b ? rgb565(0, 63, 0) : x ? rgb565(0, 0, 31) : y ? rgb565(31, 63, 0) : rgb565(31, 63, 31);
    const cx = Math.round(this.x);
    const cy = Math.round(this.y);
    for (let dy = -4; dy <= 4; dy++) {
      const py = cy + dy;
      if (py < 0 || py >= h) continue;
      for (let dx = -4; dx <= 4; dx++) {
        const px = cx + dx;
        if (px < 0 || px >= w) continue;
        fb[py * w + px] = color;
      }
    }

    // 上部に 1 秒周期のティックバー（フレームペーシングの目視確認用）
    const barW = Math.floor((this.frameCount % Math.round(this.fps)) / this.fps * w);
    for (let px = 0; px < barW; px++) {
      fb[px] = rgb565(31, 63, 31);
      fb[w + px] = rgb565(31, 63, 31);
    }
  }

  private renderAudio(a: boolean, b: boolean): void {
    const buf = this.audioBuf;
    const frames = buf.length >> 1;
    const base = a ? 880 : b ? 440 : 220;
    const amp = a || b ? 6000 : 1500;
    const step = (base * 2 * Math.PI) / SAMPLE_RATE;

    for (let i = 0; i < frames; i++) {
      this.phase += step;
      if (this.phase > Math.PI * 2) this.phase -= Math.PI * 2;
      // 矩形波っぽさを残しつつ耳に痛くない三角波
      const s = Math.round(Math.asin(Math.sin(this.phase)) * (2 / Math.PI) * amp);
      buf[i * 2] = s;
      buf[i * 2 + 1] = s;
    }
  }

  drainAudio(): AudioChunk | null {
    return { samples: this.audioBuf, sampleRate: SAMPLE_RATE };
  }

  reset(): void {
    this.x = Math.floor(this.width / 2);
    this.y = Math.floor(this.height / 2);
    this.frameCount = 0;
    this.phase = 0;
    this.hue = this.romChecksum & 0x1f;
  }

  saveState(): Uint8Array {
    const buf = new ArrayBuffer(16);
    const view = new DataView(buf);
    view.setUint32(0, 0x54455354, false); // "TEST"
    view.setInt16(4, Math.round(this.x), true);
    view.setInt16(6, Math.round(this.y), true);
    view.setUint32(8, this.frameCount, true);
    view.setUint16(12, this.hue, true);
    return new Uint8Array(buf);
  }

  loadState(state: Uint8Array): void {
    if (state.length < 16) throw new Error('invalid save state');
    const view = new DataView(state.buffer, state.byteOffset, state.byteLength);
    if (view.getUint32(0, false) !== 0x54455354) throw new Error('save state magic mismatch');
    this.x = view.getInt16(4, true);
    this.y = view.getInt16(6, true);
    this.frameCount = view.getUint32(8, true);
    this.hue = view.getUint16(12, true);
  }

  getSram(): Uint8Array | null {
    const buf = new ArrayBuffer(4);
    const view = new DataView(buf);
    view.setInt16(0, Math.round(this.x), true);
    view.setInt16(2, Math.round(this.y), true);
    return new Uint8Array(buf);
  }

  setSram(data: Uint8Array): boolean {
    if (data.length < 4) return false;
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    this.x = view.getInt16(0, true);
    this.y = view.getInt16(2, true);
    return true;
  }

  dispose(): void {
    this.framebuffer = new Uint16Array(0);
    this.audioBuf = new Int16Array(0);
  }
}

function rgb565(r: number, g: number, b: number): number {
  return ((r & 0x1f) << 11) | ((g & 0x3f) << 5) | (b & 0x1f);
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
